import {
  Alert,
  Descriptions,
  Input,
  Modal,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Typography,
  message,
} from 'antd'
import { escapeRegExp } from 'lodash'
import { useEffect, useState } from 'react'

import { EPLATFORM } from '../../../../../constants/platform'
import useAxios, { axiosInstance } from '../../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../../types/pagination'
import { IProduct, IUsedPlatformLink } from '../../../../../types/product'
import { ITikTokAccount } from '../../../../../types/tiktok'
import { numberFormat } from '../../../../../utils'
import {
  ISkuLinkTarget,
  buildTiktokSkuLink,
  findLinkConflict,
} from '../../../../../utils/platform-link.util'
import { IListUnlinkedSkusResult } from '../types'

interface Props {
  open: boolean
  target: ISkuLinkTarget | null
  onClose: () => void
  onLinked: (product: IProduct) => void
}

const SEARCH_PAGE_SIZE = 10

const SkuLinkModal = ({ open, target, onClose, onLinked }: Props) => {
  const [search, setSearch] = useState<string>('')
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')
  const [page, setPage] = useState<number>(1)
  const [total, setTotal] = useState<number>(0)
  const [products, setProducts] = useState<IProduct[]>([])
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const [selected, setSelected] = useState<IProduct | null>(null)
  const [productName, setProductName] = useState<string>('')
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const { data: accounts } = useAxios<ITikTokAccount[]>(
    open && target && !target.shopName
      ? { method: 'GET', url: '/tiktok/merchant/accounts' }
      : null,
  )
  const shopName =
    target?.shopName ||
    accounts?.find((account) => account.shopId === target?.shopId)
      ?.sellerName ||
    ''

  useEffect(() => {
    if (!open) {
      return
    }
    setSearch('')
    setDebouncedSearch('')
    setPage(1)
    setSelected(null)
    setSaveError(null)
    setProductName(target?.productName ?? '')
  }, [open, target])

  // ชื่อสินค้าฝั่ง TikTok — หาจากคำสั่งซื้อที่ต้นทุนยังไม่ครบ (มีแต่ SKU ที่ยังไม่ผูก)
  // หาไม่เจอก็ผูกได้ ชื่อเป็นแค่ป้ายให้อ่านง่าย
  useEffect(() => {
    if (!open || !target?.skuId || target.productName) {
      return
    }
    let cancelled = false
    axiosInstance
      .get<IListUnlinkedSkusResult>('/tiktok/report/orders/unlinked-skus', {
        params: target.shopId ? { shopId: target.shopId } : {},
      })
      .then((res) => {
        const found = res.data?.items?.find(
          (item) => item.sku_id === target.skuId,
        )
        if (!cancelled && found?.product_name) {
          setProductName(found.product_name)
        }
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [open, target])

  // หน่วงเฉพาะตอนพิมพ์ — กดเปลี่ยนหน้าต้องดึงทันที
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 400)
    return () => clearTimeout(handler)
  }, [search])

  useEffect(() => {
    if (!open) {
      return
    }
    let cancelled = false
    setIsSearching(true)
    axiosInstance
      .get<PaginationResponseInterface<IProduct>>('/product', {
        params: {
          page,
          perPage: SEARCH_PAGE_SIZE,
          // product-service เอาคำค้นไปทำ RegExp ตรง ๆ — รหัสอย่าง "รังนก (CG)" จะค้นเพี้ยน
          search: escapeRegExp(debouncedSearch.trim()),
          startDate: '',
          endDate: '',
        },
      })
      .then(({ data }) => {
        if (!cancelled) {
          setProducts(data?.records ?? [])
          setTotal(data?.count ?? 0)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProducts([])
          setTotal(0)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSearching(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [open, debouncedSearch, page])

  const handleLink = async () => {
    if (!target || !selected) {
      return
    }
    setIsSaving(true)
    setSaveError(null)
    try {
      // เช็กกับข้อมูลล่าสุดก่อนเขียน — ระหว่างเปิดหน้าต่างอาจมีคนผูกไปแล้ว
      const [{ data: usedLinks }, { data: product }] = await Promise.all([
        axiosInstance.get<IUsedPlatformLink[]>('/product/platform-links/used', {
          params: { platform: EPLATFORM.TIKTOK },
        }),
        axiosInstance.get<IProduct>(`/product/${selected.objectId}`),
      ])
      const conflict = findLinkConflict(usedLinks ?? [], target)
      if (conflict) {
        setSaveError(
          `SKU นี้ผูกกับ ${
            conflict.productName || conflict.productCode || '-'
          } อยู่แล้ว — ถ้าผูกผิดตัว ให้แก้ที่สินค้านั้นก่อน`,
        )
        return
      }
      const link = buildTiktokSkuLink({ ...target, shopName, productName })
      await axiosInstance.put(`/product/platform-links/${selected.objectId}`, {
        platformLinks: [...(product?.platformLinks ?? []), link],
      })
      message.success(`ผูก ${link.sku || link.skuId} กับ ${selected.name} แล้ว`)
      onLinked(selected)
      onClose()
    } catch (e) {
      setSaveError('ผูกสินค้าไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
    } finally {
      setIsSaving(false)
    }
  }

  const columns: TableColumnsType<IProduct> = [
    {
      title: 'สินค้าในระบบ',
      key: 'name',
      render: (_, product) => (
        <div>
          <div>{product.name}</div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            {product.code}
            {product.isCollection ? ' · สินค้าแบบเซ็ต' : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'ผูก TikTok แล้ว',
      key: 'links',
      align: 'right',
      width: 120,
      render: (_, product) => {
        const count = (product.platformLinks ?? []).filter(
          (link) => link.platform === EPLATFORM.TIKTOK,
        ).length
        return count ? <Tag>{count} SKU</Tag> : '-'
      },
    },
    {
      title: 'สต๊อก',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      width: 90,
      render: (value: number) => numberFormat(value ?? 0, 0),
    },
  ]

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="ผูก SKU TikTok กับสินค้าในระบบ"
      width={720}
      okText="ผูก"
      cancelText="ยกเลิก"
      onOk={handleLink}
      confirmLoading={isSaving}
      okButtonProps={{ disabled: !selected }}
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="seller_sku" span={2}>
            <Typography.Text style={{ whiteSpace: 'pre' }}>
              {target?.sku || '-'}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="สินค้าใน TikTok" span={2}>
            {productName || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="sku_id">
            {target?.skuId || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="ร้าน">
            {shopName || target?.shopId || '-'}
          </Descriptions.Item>
        </Descriptions>

        <Input.Search
          allowClear
          placeholder="ค้นหาสินค้าในระบบด้วยรหัสหรือชื่อ"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {selected && (
          <Typography.Text>
            เลือกแล้ว: <b>{selected.name}</b> ({selected.code})
          </Typography.Text>
        )}

        {saveError && <Alert type="error" showIcon message={saveError} />}

        <Table
          rowKey="objectId"
          size="small"
          columns={columns}
          dataSource={products}
          loading={isSearching}
          pagination={{
            current: page,
            pageSize: SEARCH_PAGE_SIZE,
            total,
            showSizeChanger: false,
            size: 'small',
            showTotal: (count) => `ทั้งหมด ${numberFormat(count, 0)} รายการ`,
            onChange: setPage,
          }}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selected ? [selected.objectId] : [],
            onChange: (_, rows) => setSelected(rows[0] ?? null),
          }}
          onRow={(product) => ({
            onClick: () => setSelected(product),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: isSearching ? ' ' : 'ไม่พบสินค้า' }}
        />
      </Space>
    </Modal>
  )
}

export default SkuLinkModal
